<template>
  <div class="checkin-page-wrap">
    <!-- Header -->
    <header class="checkin-header">
      <div class="checkin-header__back" @click="goBack">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </div>
      <h1 class="checkin-header__title">{{ $lang.activity_daily_checkin || 'DAILY SIGN IN' }}</h1>
      <div class="checkin-header__right" @click="goRewardHistory">
        <van-icon name="clock-o" size="12" color="#ff5a1f" />
        <span>{{ $lang.reward_history || 'Reward History' }}</span>
      </div>
    </header>

    <!-- Banner Info -->
    <div class="checkin-banner">
      <h2 class="checkin-banner__title">{{ $lang.reward_your_rewards || 'Your Rewards' }}</h2>
      <p class="checkin-banner__desc">
        {{ $lang.checkin_premium_desc || 'Premium rewards are unlocked after daily payment. Accumulate 10 deposits for a Mega Spin!' }}
      </p>
    </div>
    <div v-if="readFailureMessage" class="checkin-read-warning">
      {{ readFailureMessage }}
    </div>

    <!-- Grid of Days -->
    <div v-if="loading" class="checkin-empty">
      <van-loading color="#ff5a1f" size="28" />
    </div>
    <div v-else-if="dayList.length === 0" class="checkin-empty">
      {{ readFailureMessage || ($lang.reward_no_data || 'No rewards found') }}
    </div>
    <div v-else class="checkin-grid">
      <div
        v-for="item in dayList"
        :key="item.day"
        class="checkin-card"
        :class="{
          'checkin-card--claimed': item.status === 'claimed' && !item.premiumClaimed,
          'checkin-card--claimed-both': item.status === 'claimed' && item.premiumClaimed,
          'checkin-card--active': item.status === 'active',
          'checkin-card--missed': item.status === 'missed',
          'checkin-card--locked': item.status === 'locked',
          'checkin-card--special': item.isSpecial
        }"
      >
        <!-- Day Label -->
        <span class="checkin-card__day">{{ $lang.activity_day || 'Day' }} {{ item.day }}</span>

        <!-- Claimed State -->
        <template v-if="item.status === 'claimed'">
          <div class="checkin-card__icons">
            <!-- Regular coin - always green glow when claimed -->
            <div class="checkin-card__icon-group">
              <div class="checkin-card__icon-circle checkin-card__icon-circle--claimed-coin">
                <img src="@/assets/img/activity/activity_icon/checkin/coin.png" alt="coin" width="16" height="16" />
              </div>
              <span class="checkin-card__amount-claimed-coin">{{ item.regularAmount }} {{ $lang.checkin_coins || 'Coins' }}</span>
            </div>
            <!-- Diamond - gold glow if premium claimed, claim button if premium is writable and enabled, deposit tip otherwise -->
            <div v-if="item.premiumClaimed" class="checkin-card__icon-group">
              <div class="checkin-card__icon-circle checkin-card__icon-circle--claimed-diamond">
                <img src="@/assets/img/activity/activity_icon/checkin/diamond.png" alt="diamond" width="16" height="16" />
              </div>
              <span class="checkin-card__amount-claimed-diamond">{{ item.premiumAmount }} {{ $lang.checkin_coins || 'Coins' }}</span>
            </div>
            <div v-else class="checkin-card__icon-group checkin-card__icon-group--clickable" @click="item.premiumAvailable ? handleClaim('deposit', item) : handlePremiumTip()">
              <div class="checkin-card__icon-circle" :class="item.premiumAvailable ? 'checkin-card__icon-circle--claimed-diamond' : 'checkin-card__icon-circle--premium-diamond'">
                <img src="@/assets/img/activity/activity_icon/checkin/diamond.png" alt="diamond" width="16" height="16" />
                <van-icon v-if="!item.premiumAvailable" name="lock" size="8" color="#ffffff" class="checkin-card__mini-lock" />
              </div>
              <span class="checkin-card__amount-premium-locked">{{ item.premiumAmount }} {{ $lang.checkin_coins || 'Coins' }}</span>
            </div>
          </div>
          <span v-if="item.premiumClaimed" class="checkin-card__status checkin-card__status--claimed-both">{{ $lang.reward_claimed || 'CLAIMED' }}</span>
          <button v-else-if="item.premiumAvailable" class="checkin-card__claim-btn checkin-card__claim-btn--premium-small" :disabled="!!claimingType" @click="handleClaim('deposit', item)">
            {{ claimingType === 'deposit' ? ($lang.common_loading || 'Loading...') : ($lang.checkin_deposit_for || 'DEPOSIT FOR') + ' ' + item.premiumAmount + ' ' + ($lang.checkin_coins || 'Coins') }}
          </button>
          <button v-else-if="canRechargeForPremium()" class="checkin-card__claim-btn checkin-card__claim-btn--premium-small" @click="handlePremiumTip">
            {{ $lang.reward_go_deposit || 'Go To Deposit' }}
          </button>
          <span v-else class="checkin-card__status checkin-card__status--locked">{{ readOnlyMessage || ($lang.reward_locked || 'LOCKED') }}</span>
        </template>

        <!-- Active State -->
        <template v-else-if="item.status === 'active'">
          <div class="checkin-card__active-content">
            <!-- Regular Claim -->
            <div class="checkin-card__claim-group">
              <div class="checkin-card__icon-circle checkin-card__icon-circle--active-coin">
                <img src="@/assets/img/activity/activity_icon/checkin/coin.png" alt="coin" width="16" height="16" />
              </div>
              <button class="checkin-card__claim-btn checkin-card__claim-btn--regular" :disabled="!!claimingType" @click="handleClaim('regular')">
                {{ claimingType === 'regular' ? ($lang.common_loading || 'Loading...') : ($lang.reward_claim_coins || 'CLAIM') + ' ' + item.regularAmount + ' ' + ($lang.checkin_coins || 'Coins') }}
              </button>
            </div>
            <!-- Premium Claim: only enabled when Ashe reports premium sign as supported, writable, and enabled -->
            <div class="checkin-card__claim-group">
              <div class="checkin-card__icon-circle" :class="item.premiumAvailable ? 'checkin-card__icon-circle--claimed-diamond' : 'checkin-card__icon-circle--premium-diamond'">
                <img src="@/assets/img/activity/activity_icon/checkin/diamond.png" alt="diamond" width="16" height="16" />
                <van-icon v-if="!item.premiumAvailable" name="lock" size="8" color="#ffffff" class="checkin-card__mini-lock" />
              </div>
              <button
                v-if="item.premiumAvailable"
                class="checkin-card__claim-btn checkin-card__claim-btn--premium"
                :disabled="!!claimingType"
                @click="handleClaim('deposit', item)"
              >
                {{ claimingType === 'deposit' ? ($lang.common_loading || 'Loading...') : ($lang.checkin_deposit_for || 'DEPOSIT FOR') + ' ' + item.premiumAmount + ' ' + ($lang.checkin_coins || 'Coins') }}
              </button>
              <button
                v-else-if="canRechargeForPremium()"
                class="checkin-card__claim-btn checkin-card__claim-btn--premium"
                @click="handlePremiumTip"
              >
                {{ $lang.checkin_deposit_for || 'DEPOSIT FOR' }} {{ item.premiumAmount }} {{ $lang.checkin_coins || 'Coins' }}
              </button>
              <span v-else class="checkin-card__status checkin-card__status--locked">{{ readOnlyMessage || ($lang.reward_locked || 'LOCKED') }}</span>
            </div>
          </div>
        </template>

        <!-- Missed State (过去未签到) -->
        <template v-else-if="item.status === 'missed'">
          <div class="checkin-card__locked-icon">
            <van-icon name="cross" size="20" color="var(--vk-danger)" />
          </div>
          <div class="checkin-card__locked-amounts">
            <span class="checkin-card__amount-small">{{ item.regularAmount }} {{ $lang.checkin_coins || 'Coins' }}</span>
            <span class="checkin-card__amount-small">{{ item.premiumAmount }} {{ $lang.checkin_coins || 'Coins' }}</span>
          </div>
          <span class="checkin-card__status checkin-card__status--missed">{{ $lang.checkin_missed || 'MISSED' }}</span>
        </template>

        <!-- Locked State -->
        <template v-else-if="item.status === 'locked'">
          <div class="checkin-card__locked-icon">
            <van-icon v-if="item.isSpecial" name="lock" size="24" color="#fbbf24" />
            <template v-else>
              <van-icon name="lock" size="20" color="var(--vk-t3)" />
            </template>
          </div>
          <div class="checkin-card__locked-amounts">
            <span :class="item.isSpecial ? 'checkin-card__amount-special-dim' : 'checkin-card__amount-small'">{{ item.regularAmount }} {{ $lang.checkin_coins || 'Coins' }}</span>
            <span :class="item.isSpecial ? 'checkin-card__amount-special' : 'checkin-card__amount-premium-locked'">
              {{ item.premiumAmount }} {{ $lang.checkin_coins || 'Coins' }}
            </span>
          </div>
          <span class="checkin-card__status" :class="{ 'checkin-card__status--special': item.isSpecial }">
            {{ $lang.reward_locked || 'LOCKED' }}
          </span>
        </template>
      </div>
    </div>

    <!-- Bottom CTA -->
    <div v-if="canRechargeForPremium()" class="checkin-bottom-cta">
      <button class="checkin-bottom-cta__btn" @click="goDeposit">
        {{ $lang.reward_go_deposit || 'Go To Deposit' }}
      </button>
    </div>
  </div>
</template>

<script>
import { GetSignDaily, SignDaily } from '@/api/common'
import { reportPromoPanel } from '@/utils/common'
import {
  failMessage as rewardFailMessage,
  hasRewardIssueProof,
  hasSignRecordProof,
  hasUnavailableMarker,
  isClaimSuccess as rewardClaimSuccess,
  isNeedLogin,
  isOkResponse,
  responseContent
} from './rewardApi'

export default {
  name: 'DailyCheckIn',
  data() {
    return {
      loading: false,
      dayList: [],
      claimingType: '',
      premiumSignSupported: false,
      premiumSignEnabled: false,
      rewardWritable: true,
      readOnlyMessage: '',
      readFailureMessage: ''
    }
  },
  async mounted() {
    reportPromoPanel('sign_daily', 1)
    await this.fetchSignData()
  },
  beforeDestroy() {
    reportPromoPanel('sign_daily', 2)
  },
  methods: {
    requireLogin() {
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    safeNumber(value, fallback = 0) {
      const n = Number(typeof value === 'string' ? value.replace(/,/g, '') : value)
      return Number.isFinite(n) ? n : fallback
    },
    firstValue(source, keys, fallback = '') {
      if (!source) return fallback
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key]
      }
      return fallback
    },
    firstArray(source, keys) {
      if (!source || typeof source !== 'object') return []
      for (const key of keys) {
        const value = source[key]
        if (Array.isArray(value)) return value
        if (value && typeof value === 'object') {
          for (const nestedKey of ['list', 'records', 'dataList', 'rows', 'items']) {
            if (Array.isArray(value[nestedKey])) return value[nestedKey]
          }
        }
      }
      return []
    },
    failMessage(res, fallback) {
      const defaultFallback = fallback || this.$lang.mc_claim_failed || 'Reward is temporarily unavailable. Please try again later.'
      const message = rewardFailMessage(res, defaultFallback)
      if (this.hasWriteGuardMarker(res) && message === defaultFallback) {
        return rewardFailMessage(this.hasReadOnlyModeMarker(res) ? { readOnly: true } : { mock: true }, defaultFallback)
      }
      return message
    },
    preserveReadFailureMessage(message) {
      const base = message || this.$lang.reward_no_data || 'No rewards found'
      if (!this.dayList.length || String(base).includes('已保留已加载签到数据')) return base
      return `${base}，已保留已加载签到数据。`
    },
    setReadFailure(res, fallback) {
      this.readFailureMessage = this.preserveReadFailureMessage(this.failMessage(res, fallback || this.$lang.reward_no_data || 'No rewards found'))
      this.$toast({ message: this.readFailureMessage, icon: 'cross' })
    },
    failCloseStaleSignRows() {
      this.rewardWritable = false
      this.premiumSignEnabled = false
      this.dayList = this.dayList.map(item => ({
        ...item,
        status: item.status === 'active' ? 'locked' : item.status,
        premiumAvailable: false
      }))
    },
    hasWriteGuardMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      if (hasUnavailableMarker(source)) return true
      const blockingKeys = ['readOnly', 'readOnlyMode', 'readonly', 'read_only', 'read_only_mode', 'mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported', 'failed', 'failClosed', 'fail_closed', 'writeBlocked', 'write_blocked', 'technicalMarker', 'technical_marker', 'technicalException', 'technical_exception', 'backendFailure', 'backend_failure', 'fallback', 'fallbackUsed', 'fallback_used', 'dataUnavailable', 'data_unavailable', 'falseProof', 'false_proof', 'paymentStatus', 'payment_status', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed', 'unconfirmed']
      const blockingTokens = ['mock', 'mock_mode', 'mock mode', 'mock-mode', 'mock_fallback', 'read_only', 'read_only_mode', 'read only mode', 'read-only-mode', 'readonly', 'read only', 'read-only', 'stub', 'unimplemented', 'not_implemented', 'not_supported', 'not supported', 'unsupported', 'failed', 'failclosed', 'fail_closed', 'fail closed', 'fail-closed', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'writeproof:false', 'write_proof:false', 'write proof:false', 'write-proof:false', 'writeproof false', 'write proof false', 'write-proof-false', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'data_unavailable', 'data unavailable', 'data-unavailable', 'dataunavailable', 'falseproof', 'false_proof', 'false proof', 'false-proof', 'technicalmarker', 'technical_marker', 'technical marker', 'technical-marker', 'technicalexception', 'technical_exception', 'technical exception', 'technical-exception', 'backendfailure', 'backend_failure', 'backend failure', 'backend-failure', '技术异常', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'payment status unconfirmed', 'payment-status-unconfirmed', 'unconfirmed']
      const isActive = value => value === true || value === 1 || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      const isFalseProof = value => value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase()) || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (['writeProof', 'write_proof', 'rewardProof', 'reward_proof'].some(key => isFalseProof(source[key]))) return true
      if (blockingKeys.some(key => isActive(source[key]))) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.errorMessage, source.errorMsg, source.reason, source.desc, source.description, source.detail, source.readOnlyMessage, source.unavailableReason].some(isActive)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasWriteGuardMarker(source[key], depth + 1))
    },
    hasHardReadBlockMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      const keys = ['mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported', 'failed', 'failClosed', 'fail_closed', 'writeBlocked', 'write_blocked', 'technicalMarker', 'technical_marker', 'technicalException', 'technical_exception', 'backendFailure', 'backend_failure', 'fallback', 'fallbackUsed', 'fallback_used', 'dataUnavailable', 'data_unavailable', 'falseProof', 'false_proof', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed', 'unconfirmed']
      const tokens = ['mock', 'mock_mode', 'mock mode', 'mock-mode', 'mock_fallback', 'stub', 'unimplemented', 'not_implemented', 'not_supported', 'not supported', 'unsupported', 'failed', 'failclosed', 'fail_closed', 'fail closed', 'fail-closed', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'writeproof:false', 'write_proof:false', 'write proof:false', 'write-proof:false', 'writeproof false', 'write proof false', 'write-proof-false', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'data_unavailable', 'data unavailable', 'data-unavailable', 'dataunavailable', 'falseproof', 'false_proof', 'false proof', 'false-proof', 'technicalmarker', 'technical_marker', 'technical marker', 'technical-marker', 'technicalexception', 'technical_exception', 'technical exception', 'technical-exception', 'backendfailure', 'backend_failure', 'backend failure', 'backend-failure', '技术异常', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'payment status unconfirmed', 'payment-status-unconfirmed', 'unconfirmed']
      const isActive = value => value === true || value === 1 || tokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      const isFalseProof = value => value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase()) || tokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (['writeProof', 'write_proof', 'rewardProof', 'reward_proof'].some(key => isFalseProof(source[key]))) return true
      if (keys.some(key => isActive(source[key]))) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.errorMessage, source.errorMsg, source.reason, source.desc, source.description, source.detail, source.unavailableReason].some(isActive)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasHardReadBlockMarker(source[key], depth + 1))
    },
    hasReadOnlyModeMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      const isReadOnlyMode = value => ['readonlymode', 'read_only_mode', 'read only mode', 'read-only-mode'].some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (source.readOnlyMode === true || source.readOnlyMode === 1 || source.read_only_mode === true || source.read_only_mode === 1) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.errorMessage, source.errorMsg, source.reason, source.desc, source.description, source.detail, source.readOnlyMessage, source.unavailableReason].some(isReadOnlyMode)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasReadOnlyModeMarker(source[key], depth + 1))
    },
    isSignSuccess(res, claimType) {
      if (this.hasWriteGuardMarker(res)) return false
      return rewardClaimSuccess(res, (c) => {
        const statusSuccess = claimType === 0
          ? (Number(c.signDailyStatus) === 1 || Number(c.isFreeSign) === 1)
          : Number(c.isPaySign) === 1
        return statusSuccess
      }) && (hasRewardIssueProof(res) || hasSignRecordProof(res))
    },
    goBack() {
      this.$router.go(-1)
    },
    goRewardHistory() {
      this.$jumpTo('/rewardHistory')
    },
    goDeposit() {
      if (!this.canRechargeForPremium()) {
        this.showPremiumUnavailable()
        return
      }
      this.$jumpTo('/rechargeCont')
    },
    canRechargeForPremium() {
      return this.premiumSignSupported && this.rewardWritable
    },
    isFalseFlag(value) {
      return value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase())
    },
    isTrueFlag(value) {
      return value === true || value === 1 || ['1', 'true', 'yes', 'on'].includes(String(value == null ? '' : value).trim().toLowerCase())
    },
    safeReadOnlyMessage(value, fallback) {
      const text = String(value == null ? '' : value).trim()
      const token = text.toLowerCase()
      if (!text || token === 'null' || token === 'undefined' || this.hasWriteGuardMarker({ readOnlyMessage: text })) {
        return fallback || ''
      }
      return text
    },
    isPositiveStatus(value) {
      const token = String(value == null ? '' : value).trim().toLowerCase()
      return this.safeNumber(value) === 1 || ['true', 'yes', 'on', 'claimable', 'available', 'signed', 'claimed', 'received', 'completed'].includes(token)
    },
    dateParts(value) {
      if (!value) return null
      if (value instanceof Date && !isNaN(value.getTime())) {
        return { year: value.getFullYear(), month: value.getMonth(), day: value.getDate() }
      }
      const token = String(value).trim()
      const dateOnly = token.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
      if (dateOnly) {
        return {
          year: Number(dateOnly[1]),
          month: Number(dateOnly[2]) - 1,
          day: Number(dateOnly[3])
        }
      }
      const parsed = new Date(token)
      if (isNaN(parsed.getTime())) return null
      return { year: parsed.getFullYear(), month: parsed.getMonth(), day: parsed.getDate() }
    },
    currentSignDateParts(content) {
      const keys = ['serverDate', 'today', 'currentDate', 'currentDay', 'signDate', 'businessDate', 'nowDate', 'date']
      for (const key of keys) {
        const parts = this.dateParts(content && content[key])
        if (parts) return parts
      }
      return this.dateParts(new Date())
    },
    isSameMonth(left, right) {
      return left && right && left.year === right.year && left.month === right.month
    },
    isRewardWritableSource(source) {
      if (!source || typeof source !== 'object') return false
      return !this.hasWriteGuardMarker(source) &&
        !this.isFalseFlag(source.rewardWritable) &&
        !this.isFalseFlag(source.writable) &&
        !this.isFalseFlag(source.writeEnabled)
    },
    isPremiumClaimAvailable(item) {
      if (!item) return false
      const paySignReady = this.safeNumber(item.paySignStatus) === 1 || this.isPositiveStatus(item.paySignStatus)
      return this.canRechargeForPremium() && this.premiumSignEnabled && paySignReady && this.isRewardWritableSource(item)
    },
    shouldPreserveUnavailableRead(rows) {
      return this.dayList.length > 0 && rows.length === 0
    },
    shouldBlockReadPayload(res, content, rows = []) {
      if (!this.hasWriteGuardMarker(res) && !this.hasWriteGuardMarker(content)) return false
      if (rows.length > 0 && !this.hasHardReadBlockMarker(res) && !this.hasHardReadBlockMarker(content)) return false
      this.setReadFailure(res, this.$lang.reward_no_data || 'No rewards found')
      return true
    },
    showPremiumUnavailable() {
      this.$toast({
        message: this.readOnlyMessage || this.$lang.reward_not_available || 'This feature is temporarily unavailable. Please try again later.',
        icon: 'warning-o'
      })
    },
    async fetchSignData() {
      try {
        this.loading = true
        const res = await GetSignDaily()
        if (isNeedLogin(res)) {
          this.failCloseStaleSignRows()
          this.requireLogin()
        } else if (isOkResponse(res)) {
          const content = responseContent(res)
          const signList = Array.isArray(content.signList) ? content.signList : []
          const normalizedSignList = signList.length ? signList : this.firstArray(content, ['records', 'list', 'dataList', 'rows', 'items'])
          if (this.shouldBlockReadPayload(res, content, normalizedSignList)) return
          if (this.shouldPreserveUnavailableRead(normalizedSignList)) {
            this.setReadFailure(res, this.$lang.reward_no_data || 'No rewards found')
            return
          }
          this.readFailureMessage = ''
          this.premiumSignSupported = !this.isFalseFlag(content.premiumSignSupported)
          this.premiumSignEnabled = this.isTrueFlag(content.premiumSignEnabled)
          this.rewardWritable = !this.isFalseFlag(content.rewardWritable)
          const contentWritable = this.rewardWritable && this.isRewardWritableSource(content)
          this.premiumSignEnabled = this.premiumSignEnabled && contentWritable
          this.rewardWritable = contentWritable
          this.readOnlyMessage = content.readOnlyMessage || content.rewardMessage || ''
          this.readOnlyMessage = this.safeReadOnlyMessage(this.readOnlyMessage, contentWritable ? '' : this.failMessage(content, ''))
          const todayParts = this.currentSignDateParts(content)
          const todayDay = todayParts.day
          const year = todayParts.year
          const month = todayParts.month
          const daysInMonth = new Date(year, month + 1, 0).getDate()
          const startDay = Math.max(1, todayDay - 3)

          this.dayList = normalizedSignList
            .filter(item => {
              const dateValue = this.firstValue(item, ['date', 'businessDate', 'business_date', 'signDate', 'sign_date', 'dayDate', 'day_date', 'createDate'], '')
              if (!item || !dateValue) return false
              const parts = this.dateParts(dateValue)
              if (!this.isSameMonth(parts, todayParts)) return false
              const day = parts.day
              return day >= startDay && day <= daysInMonth
            })
            .map((item, index, arr) => {
              const dateValue = this.firstValue(item, ['date', 'businessDate', 'business_date', 'signDate', 'sign_date', 'dayDate', 'day_date', 'createDate'], '')
              const parts = this.dateParts(dateValue)
              const day = parts.day
              const regularAmount = this.safeNumber(this.firstValue(item, ['freeSignReward', 'freeRewardAmount', 'regularAmount', 'rewardAmount', 'reward', 'amount'], 0))
              const premiumAmount = this.safeNumber(this.firstValue(item, ['paySignReward', 'premiumRewardAmount', 'premiumAmount', 'payRewardAmount', 'diamondReward'], 0))
              const isSpecial = index === arr.length - 1

              let status = 'locked'
              let premiumClaimed = false
              const rowWritable = contentWritable && this.isRewardWritableSource(item)
              const freeStatus = this.firstValue(item, ['isFreeSign', 'signDailyStatus', 'freeSignStatus', 'free_sign_status', 'signed', 'freeClaimed'], 0)
              const premiumStatus = this.firstValue(item, ['isPaySign', 'paySignStatus', 'premiumSignStatus', 'premium_sign_status', 'premiumClaimed', 'payClaimed'], 0)
              const normalizedPaySignStatus = this.isPositiveStatus(premiumStatus) ? 1 : this.safeNumber(premiumStatus)
              item.paySignStatus = normalizedPaySignStatus

              if (this.isPositiveStatus(freeStatus)) {
                status = 'claimed'
                premiumClaimed = this.safeNumber(premiumStatus) === 2 || this.isPositiveStatus(this.firstValue(item, ['isPaySign', 'premiumClaimed', 'payClaimed'], 0))
              } else if (day === todayDay && rowWritable) {
                status = 'active'
              } else if (day < todayDay) {
                status = 'missed'
              } else {
                status = 'locked'
              }

              return {
                day,
                regularAmount,
                premiumAmount,
                status,
                premiumClaimed,
                isSpecial,
                date: dateValue,
                paySignStatus: normalizedPaySignStatus,
                premiumAvailable: this.isPremiumClaimAvailable(item)
              }
            })
        } else {
          this.failCloseStaleSignRows()
          this.setReadFailure(res, this.$lang.mc_claim_failed || 'This feature is temporarily unavailable. Please try again later.')
        }
      } catch (e) {
        this.setReadFailure(e, '活动请求失败，请稍后重试。')
      } finally {
        this.loading = false
      }
    },
    async handleClaim(type, item = null) {
      if (this.claimingType) return
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (!this.rewardWritable) {
        this.showPremiumUnavailable()
        return
      }
      if (type === 'deposit' && !this.isPremiumClaimAvailable(item)) {
        this.showPremiumUnavailable()
        return
      }
      this.claimingType = type
      try {
        const claimType = type === 'regular' ? 0 : 1
        const res = await SignDaily({ type: claimType })
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (this.isSignSuccess(res, claimType)) {
          this.$toast({ message: this.$lang.activity_checkin_success || 'Check-in successful!', icon: 'success' })
          await this.fetchSignData()
        } else {
          this.$toast({ message: this.failMessage(res, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        const networkFallback = this.$lang.network_error === 'Network error'
          ? '活动网络请求失败，请稍后重试。'
          : (this.$lang.network_error || '活动网络请求失败，请稍后重试。')
        this.$toast({ message: this.failMessage(e, networkFallback), icon: 'cross' })
      } finally {
        this.claimingType = ''
      }
    },
    handlePremiumTip() {
      if (!this.canRechargeForPremium()) {
        this.showPremiumUnavailable()
        return
      }
      this.$toast({ message: this.$lang.reward_deposit_tip || 'Deposit today to unlock premium rewards!', icon: 'warning-o' })
      this.goDeposit()
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;   /* 柠檬绿→火焰橙(强调/激活) */
@bg-dark: @vk-bg;   /* 页面底 */
@surface: rgba(255, 255, 255, 0.03);

.checkin-page-wrap {
  min-height: 100vh;
  background: @bg-dark;
  padding-bottom: 100px;
}

// Header
.checkin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 50;
  background: fade(@bg-dark, 80%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &__back {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 50%;
    &:active {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  &__title {
    color: #ffffff;
    font-size: var(--vk-fs-base);
    font-weight: bold;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    flex: 1;
    text-align: center;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    span {
      color: @primary;
      font-size: var(--vk-fs-2xs);
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }
}

// Banner
.checkin-banner {
  padding: 24px 20px 16px;

  &__title {
    color: #ffffff;
    font-size: var(--vk-fs-2xl);
    font-weight: bold;
    margin-bottom: 8px;
  }

  &__desc {
    color: var(--vk-t2);
    font-size: var(--vk-fs-xs);
    line-height: 1.5;
  }
}

// Grid
.checkin-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 8px 20px 20px;
}

.checkin-empty {
  padding: 40px 20px;
  text-align: center;
  color: var(--vk-t2);
  font-size: var(--vk-fs-sm);
}

.checkin-read-warning {
  margin: 0 20px 8px;
  padding: 10px 12px;
  border: 1px solid fade(#fbbf24, 30%);
  border-radius: 8px;
  background: fade(#fbbf24, 8%);
  color: #fbbf24;
  font-size: var(--vk-fs-xs);
  line-height: 1.4;
}

// Card Base
.checkin-card {
  background: @surface;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  &__day {
    font-size: var(--vk-fs-2xs);
    font-weight: bold;
    text-transform: uppercase;
    color: var(--vk-t2);
  }

  // Claimed (regular only - Day 1 style)
  &--claimed {
    opacity: 0.7;
    border-color: fade(@primary, 20%);
  }

  // Claimed (both regular + premium - Day 2 style)
  &--claimed-both {
    opacity: 0.9;
    border-color: fade(@primary, 40%);
    background: fade(@primary, 5%);
  }

  // Active (neon glow)
  &--active {
    border-color: fade(@primary, 40%);
    background: fade(@primary, 5%);
    box-shadow: 0 0 10px fade(@primary, 60%), inset 0 0 5px fade(@primary, 30%);

    .checkin-card__day {
      color: @primary;
    }
  }

  // Missed (过去未签到)
  &--missed {
    opacity: 0.5;
    border-color: fade(#f5455c, 15%);
  }

  // Special (Day 12)
  &--special {
    background: linear-gradient(135deg, fade(#fbbf24, 5%), transparent);
    border-color: fade(#fbbf24, 20%);

    .checkin-card__day {
      color: #fbbf24;
    }

    .checkin-card__locked-icon {
      background: fade(#fbbf24, 10%);
      border-color: fade(#fbbf24, 30%);
    }
  }

  // Icons row (claimed state)
  &__icons {
    display: flex;
    gap: 12px;
    width: 100%;
    justify-content: center;
  }

  &__icon-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__icon-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &--coin {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    &--diamond {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    &--claimed-coin {
      background: fade(@primary, 20%);
      border: 1px solid fade(@primary, 50%);
      box-shadow: 0 0 15px fade(@primary, 40%);
    }

    &--claimed-diamond {
      background: fade(#FFD700, 20%);
      border: 1px solid fade(#FFD700, 50%);
      box-shadow: 0 0 15px fade(#FFD700, 40%);
    }

    &--active-coin {
      background: fade(@primary, 20%);
      border: 1px solid fade(@primary, 50%);
      box-shadow: 0 0 15px fade(@primary, 40%);
    }

    &--premium-diamond {
      background: fade(#fbbf24, 10%);
      border: 1px solid fade(#fbbf24, 30%);
      position: relative;
    }
  }

  &__mini-lock {
    position: absolute;
  }

  &__amount-small {
    font-size: 8px;
    color: var(--vk-t3);
    font-weight: 500;
  }

  &__amount-premium-locked {
    font-size: 8px;
    color: fade(#f59e0b, 50%);
    font-weight: 500;
  }

  &__amount-claimed-coin {
    font-size: 8px;
    color: @primary;
    font-weight: bold;
  }

  &__amount-claimed-diamond {
    font-size: 8px;
    color: #FFD700;
    font-weight: bold;
  }

  &__icon-group--dimmed {
    opacity: 0.3;
  }

  &__icon-group--clickable {
    cursor: pointer;
    &:active {
      opacity: 0.7;
    }
  }

  &__amount-special {
    font-size: 8px;
    color: #fbbf24;
    font-weight: bold;
  }

  &__amount-special-dim {
    font-size: 8px;
    color: fade(#fbbf24, 60%);
    font-weight: 500;
  }

  &__status {
    font-size: var(--vk-fs-2xs);
    color: #475569;
    text-transform: uppercase;
    font-weight: bold;
    margin-top: 4px;
    letter-spacing: 0.05em;

    &--claimed {
      color: @primary;
      letter-spacing: 0.05em;
    }

    &--special {
      color: #fbbf24;
      font-weight: 900;
      letter-spacing: 0.1em;
    }

    &--claimed-both {
      color: @primary;
      font-weight: 900;
      letter-spacing: 0.15em;
    }

    &--missed {
      color: var(--vk-danger);
      font-weight: 600;
    }
  }

  // Active content
  &__active-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    align-items: center;
  }

  &__claim-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  &__claim-btn {
    width: 100%;
    border: none;
    font-size: 8px;
    font-weight: 900;
    padding: 6px 4px;
    border-radius: 999px;
    text-transform: uppercase;
    cursor: pointer;
    text-align: center;
    line-height: 1.2;

    &--regular {
      background: @primary;
      color: @bg-dark;
    }

    &--premium {
      background: #fbbf24;
      color: @bg-dark;
      font-size: 7px;
    }

    &--premium-small {
      background: #fbbf24;
      color: @bg-dark;
      font-size: 7px;
      width: 100%;
      border: none;
      padding: 4px 4px;
      border-radius: 999px;
      text-transform: uppercase;
      cursor: pointer;
      text-align: center;
      font-weight: 900;
      line-height: 1.2;
    }
  }

  // Locked content
  &__locked-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__locked-amounts {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }
}

// Bottom CTA
.checkin-bottom-cta {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: var(--vk-pc-col);
  padding: 20px;
  background: linear-gradient(to top, @bg-dark, fade(@bg-dark, 90%), transparent);
  z-index: 40;

  &__btn {
    width: 100%;
    background: @primary;
    color: @bg-dark;
    font-size: var(--vk-fs-lg);
    font-weight: 900;
    padding: 16px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: -0.02em;
    box-shadow: 0 0 20px fade(@primary, 30%);

    &:active {
      transform: scale(0.98);
    }
  }
}

// PC
@media (min-width: 769px) {
  .checkin-page-wrap {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
    border-left: 1px solid fade(#1e293b, 50%);
    border-right: 1px solid fade(#1e293b, 50%);
  }
}
</style>
